/**
 * Funnel-platform event taxonomy (PLAN.md §6.4).
 *
 * Two paths share this taxonomy:
 *   1. Analytics — every event, sunk to Workers Analytics Engine + S3 firehose.
 *   2. Conversion — only paid-ad events, sunk to browser pixels +
 *      `spicychat-backend` CAPI fan-out.
 *
 * Both paths share the same `event_id = onboarding_session_id`-derived
 * dedup contract, so this module owns the canonical event-id derivation
 * and the typed envelope every emitter wraps around the payload.
 */

import { fnv1a32 } from "@nextday/funnel-events/fnv";

export type EventName =
  | "funnel_landed"
  | "funnel_step_viewed"
  | "funnel_step_submitted"
  | "funnel_step_skipped"
  | "funnel_dropped"
  | "funnel_qualified"
  | "funnel_account_created"
  | "funnel_handoff_clicked"
  | "funnel_handoff_consumed"
  | "funnel_paid_conversion";

/**
 * Common identifiers attached to every event. The `onboarding_session_id`
 * is the dedup key everywhere — browser pixel, server CAPI, S3 firehose.
 */
export type EventContext = {
  readonly onboarding_session_id: string;
  readonly anonymous_id: string;
  readonly funnel_id: string;
  readonly override_id?: string;
  readonly application_id: string;
  readonly character_id?: string | null;
  readonly kinde_user_id?: string | null;
};

/**
 * Per-event payload. Loose Record<string, unknown> on purpose — events
 * carry diverse shapes (step_id, answer, lift_metric, …) and the
 * analytics path stores them as JSON. Stricter shapes can be enforced
 * at the call site.
 */
export type EventPayload = Record<string, unknown>;

export type FunnelEvent = {
  readonly name: EventName;
  readonly context: EventContext;
  readonly payload: EventPayload;
  /** UNIX millis. Required so client + server agree on the timeline. */
  readonly occurred_at: number;
};

/**
 * The wire envelope sent to `/api/events`. `event_id` is what consumers
 * dedup on (Meta CAPI / S3 firehose / Databricks join).
 */
export type EventEnvelope = {
  readonly event_id: string;
  readonly event: FunnelEvent;
};

/**
 * Derive the canonical `event_id` for a funnel event. Composing
 * `onboarding_session_id` + event name + occurred_at means a given
 * (session, event) pair always produces the same id even if the same
 * event is replayed by retry logic.
 *
 * Pure / sync — no Web Crypto needed; this is a stable hash, not a
 * security primitive. Using FNV-1a 32-bit for compactness on the wire.
 */
export const buildEventId = (event: FunnelEvent): string => {
  const seed = `${event.context.onboarding_session_id}:${event.name}:${event.occurred_at}`;
  return `evt_${fnv1a32(seed).toString(36)}_${event.context.onboarding_session_id}`;
};

export const wrapEnvelope = (event: FunnelEvent): EventEnvelope => ({
  event_id: buildEventId(event),
  event,
});

/** Names that contribute to a paid-ad conversion fire (PLAN.md §6.4 table). */
export const CONVERSION_EVENT_NAMES: ReadonlySet<EventName> = new Set([
  "funnel_account_created",
  "funnel_paid_conversion",
]);

export const isConversionEvent = (name: EventName): boolean => CONVERSION_EVENT_NAMES.has(name);
